import { ContainerQuery } from '@/components/container-query/ContainerQuery';
import { useClientConfigModelStore } from '@/models/client-config/clientConfig';
import type { FooterBannerQRCode } from '@/shared/constant/footer-banner/FooterBanner';
import { layoutImage } from '@/shared/constant/layoutImage';
import { BannerBackgroundThemeOptions } from '@tarslib/types/lib/publishedProject';
import clsx from 'clsx';
import React from 'react';

export type BottomBannerQRCodeProps = {
  value: FooterBannerQRCode;
  className?: string;
};

export function BottomBannerQRCode(props: BottomBannerQRCodeProps) {
  const { value, className } = props;
  const { iconUrl, title, description, QRCodeUrl, QRCodeDescription, backgroundTheme } = value;

  const apidocBuiltinPrefixPath = useClientConfigModelStore((x) => x.apidocBuiltinPrefixPath);

  const backgroundUrl = React.useMemo(() => {
    const backgroundThemeMap = {
      [BannerBackgroundThemeOptions.DARK]: `${apidocBuiltinPrefixPath}/${layoutImage.customBannerDarkBackground}`,
      [BannerBackgroundThemeOptions.LIGHT]: `${apidocBuiltinPrefixPath}/${layoutImage.customBannerLightBackground}`,
    };
    return backgroundThemeMap[backgroundTheme];
  }, [apidocBuiltinPrefixPath, backgroundTheme]);

  return (
    <ContainerQuery containerSizes={[546, 800]} className="@container/bottom-banner-qr-code">
      <div
        className={clsx(
          'flex h-[160px] max-w-full justify-center overflow-hidden rounded border border-color-split bg-cover',
          {
            ['text-[#333]']: backgroundTheme === BannerBackgroundThemeOptions.LIGHT,
            ['text-white']: backgroundTheme === BannerBackgroundThemeOptions.DARK,
          },
          className,
        )}
        style={{
          background: `url(${backgroundUrl})`,
        }}
      >
        <div className="flex p-0 @[546px]/bottom-banner-qr-code:px-[88px] @[546px]/bottom-banner-qr-code:py-0">
          <div
            className={clsx(
              'mr-8 hidden flex-1 flex-col justify-center @[546px]/bottom-banner-qr-code:flex',
            )}
          >
            <div className={'flex h-14 items-center'}>
              <img
                src={iconUrl || ''}
                className={clsx('mr-2 h-11 w-11 rounded border border-solid border-color-split', {
                  ['border-[#56575814]']: backgroundTheme === BannerBackgroundThemeOptions.LIGHT,
                  ['border-[#e6e7e914]']: backgroundTheme === BannerBackgroundThemeOptions.DARK,
                })}
                alt=""
              />
              <p
                className={clsx(
                  'm-0 flex items-center overflow-hidden overflow-ellipsis text-xl font-bold @[800px]/bottom-banner-qr-code:text-2xl',
                )}
              >
                <span className="truncate">{title}</span>
              </p>
            </div>
            <p className={clsx('mb-0 mt-2 overflow-hidden overflow-ellipsis text-semi-lg')}>
              {description}
            </p>
          </div>

          <div
            className={
              'relative flex h-full w-[200px] flex-shrink-0 flex-col justify-end pt-[14px]'
            }
          >
            <img
              src={`${apidocBuiltinPrefixPath}/customBannerPhone.png`}
              className="relative z-[2] h-full w-full"
              alt=""
            />
            <div
              className={
                'absolute bottom-0 left-[42px] z-[1] flex h-[140px] w-[113px] justify-center rounded-t-[14px] p-3 px-1.5 pb-[7px]'
              }
              style={{
                backgroundImage: `url("${apidocBuiltinPrefixPath}/customBannerPhoneBackground.png")`,
              }}
            >
              <div
                className="flex w-full flex-col items-center p-2"
                style={{ background: '#fff', borderRadius: '5px' }}
              >
                <img src={QRCodeUrl || ''} style={{ width: '100%' }} className="rounded" alt="" />
                <span className="mt-1 w-full truncate text-center text-sm font-bold text-[#564e6e]">
                  {QRCodeDescription}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ContainerQuery>
  );
}
